import { Platform } from 'react-native';
import { calculateSobrietyDays } from '@/utils/sobrietyDays';

const APP_GROUP = 'group.app.padrinho.eloapp.widget';
const DATA_KEY = 'sobrietyData';

interface SobrietyRecord {
  start_date: string;
  base_days: number;
  is_active: boolean;
}

interface WidgetSobrietyData {
  isSetup: boolean;
  totalDays: number;
  startDate: string | null;
  baseDays: number;
  lastSync: string;
}

function computeTotalDays(record: SobrietyRecord): number {
  // Single source of truth — see utils/sobrietyDays.ts
  return calculateSobrietyDays(new Date(record.start_date), record.base_days);
}

async function writeToSharedStorage(data: WidgetSobrietyData): Promise<void> {
  if (Platform.OS !== 'ios' && Platform.OS !== 'android') return;
  try {
    const SharedGroupPreferences = (await import('react-native-shared-group-preferences')).default;
    const json = JSON.stringify(data);
    await SharedGroupPreferences.setItem(DATA_KEY, json, APP_GROUP);
  } catch (err) {
    console.warn('[widgetService] Failed to write to shared storage:', err);
  }
}

async function reloadIOSWidget(): Promise<void> {
  if (Platform.OS !== 'ios') return;
  try {
    const { reloadWidget } = await import('sobriety-widget');
    reloadWidget();
  } catch {
    // sobriety-widget native module not available (Expo Go / simulator)
  }
}

export const widgetService = {
  async syncSobrietyData(record: SobrietyRecord | null, isSetup: boolean): Promise<void> {
    try {
      const data: WidgetSobrietyData = {
        isSetup: isSetup && !!record,
        totalDays: record ? computeTotalDays(record) : 0,
        startDate: record?.start_date ?? null,
        baseDays: record?.base_days ?? 0,
        lastSync: new Date().toISOString(),
      };
      await writeToSharedStorage(data);
      await reloadIOSWidget();
    } catch (err) {
      console.warn('[widgetService] syncSobrietyData failed:', err);
    }
  },

  async clearWidgetData(): Promise<void> {
    try {
      const data: WidgetSobrietyData = {
        isSetup: false,
        totalDays: 0,
        startDate: null,
        baseDays: 0,
        lastSync: new Date().toISOString(),
      };
      await writeToSharedStorage(data);
      await reloadIOSWidget();
    } catch (err) {
      console.warn('[widgetService] clearWidgetData failed:', err);
    }
  },
};
